import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { Calendar } from "@/components/ui/calendar";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { format } from "date-fns";
import { toast } from "sonner";

import api from "../utils/axios";
import { hasRole } from "../utils/roles";
import { blockNegativeKey, blockWheelChange } from "../utils/numberInput";

// ─── Dropdowns matching Admin page ────────────────────────────────────────────
const STATUSES    = ["Live", "Completed", "Paused", "Declined", "Proof Submitted"];
const PLATFORMS   = ["Promowise", "Integrate", "Direct", "Demand Works", "Intentsify", "Converter"];
const PRIORITIES  = ["NA", "P1", "P2", "P3"];
const DELIVERY_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PACING_OPTIONS = ["NA", "Daily", "Weekly", "Monthly", "Custom"];

// ─── Field-level edit permissions ──────────────────────────────────────────────
// Campaign Manager can ONLY edit Delivered + Accepted.
// Client Success Manager can edit everything EXCEPT Delivered + Accepted.
// Admin / CEO are kept as a super-user override — drop `if (isAdmin) return true;`
// below if you'd rather they follow the same restrictions as CM/CSM.
const CM_ONLY_FIELDS = new Set(["delivered", "accepted", "rejected", "pendingQA",]);

// ─── Field wrapper (label above input) ────────────────────────────────────────
const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium text-muted-foreground">
      {label}{required && <span className="text-destructive ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

export default function CampaignEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: { salesManagers: [] },
  });

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  const [clients,  setClients]  = useState([]);
  const [csmList,  setCsmList]  = useState([]);
  const [CMs,      setCMs]      = useState([]);
  const [managers, setManagers] = useState([]);

  // ─── Who's logged in, and what can they edit here? ────────────────────────
  const loggedUser = JSON.parse(localStorage.getItem("user")) || {};
  const role    = (loggedUser.role || loggedUser.designation || "").toLowerCase();
const isAdmin = hasRole(loggedUser, "admin", "ceo");
const isCM    = hasRole(loggedUser, "cm");
const isCSM   = hasRole(loggedUser, "csm");

const isFieldEditable = (field) => {
  if (isAdmin) return true;
  if (isCM  && CM_ONLY_FIELDS.has(field))  return true;
  if (isCSM && !CM_ONLY_FIELDS.has(field)) return true;
  return false;
};
  const disabledCls = "bg-muted/50 cursor-not-allowed opacity-70";

  // ─── Load campaign ───────────────────────────────────────────────────────────
  useEffect(() => {
    const loadCampaign = async () => {
      try {
        const { data } = await api.get(`/campaigns/${id}`);

        Object.entries(data).forEach(([key, value]) => {
          if (value === null) return;

          if (["liveDate", "startDate", "endDate"].includes(key)) {
            if (typeof value === "string") {
              const dateObj = new Date(value);
              if (!isNaN(dateObj.getTime())) {
                const localDate = new Date(
                  dateObj.getFullYear(),
                  dateObj.getMonth(),
                  dateObj.getDate()
                );
                setValue(key, localDate);
              }
            }
            return;
          }

          if (key === "salesManagers") {
            let parsed = [];
            if (Array.isArray(value)) {
              parsed = value;
            } else if (typeof value === "string") {
              try {
                const temp = JSON.parse(value);
                parsed = Array.isArray(temp) ? temp : [];
              } catch {
                if (value.includes(",")) {
                  parsed = value.split(",").map((i) => i.trim()).filter(Boolean);
                } else if (value.trim()) {
                  parsed = [value.trim()];
                }
              }
            }
            setValue("salesManagers", parsed, { shouldDirty: false, shouldValidate: false });
            return;
          }

          setValue(key, value);
        });
      } catch {
        toast.error("Failed to load campaign");
      } finally {
        setLoading(false);
      }
    };
    loadCampaign();
  }, [id, setValue]);

  // ─── Fetch users ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchUsers = async () => {
      try {
       const { data: allUsers } = await api.get("/users");
setClients(allUsers.filter((u) => hasRole(u, "Client")));
setCsmList(allUsers.filter((u) => hasRole(u, "CSM")));
setCMs(allUsers.filter((u) => hasRole(u, "CM")));
setManagers(allUsers.filter((u) => hasRole(u, "Manager")));
      } catch {
        console.error("Failed to fetch users");
      }
    };
    fetchUsers();
  }, []);

  // ─── Auto-calc outstanding when allocation / accepted change ─────────────────
  const allocation = watch("allocation");
  const accepted   = watch("accepted");
  useEffect(() => {
    const a = Number(allocation) || 0;
    const b = Number(accepted)   || 0;
    setValue("outstanding", a - b);
  }, [allocation, accepted, setValue]);

  // ─── Submit ──────────────────────────────────────────────────────────────────
  const onSubmit = async (form) => {
    setSaving(true);
    try {
      const { data: originalCampaign } = await api.get(`/campaigns/${id}`);

      const normDate = (d) => {
        if (!d) return null;
        const obj = d instanceof Date ? d : new Date(d);
        if (isNaN(obj.getTime())) return null;
        const y = obj.getFullYear();
        const m = String(obj.getMonth() + 1).padStart(2, "0");
        const dd = String(obj.getDate()).padStart(2, "0");
        return `${y}-${m}-${dd}`;
      };

      const hasChanged = (field, newVal, oldVal) => {
        if (field === "salesManagers") {
          const a = [...(Array.isArray(oldVal) ? oldVal : [])].sort().join(",");
          const b = [...(Array.isArray(newVal) ? newVal : [])].sort().join(",");
          return a !== b;
        }
        if (["liveDate", "startDate", "endDate"].includes(field)) {
          return normDate(oldVal) !== normDate(newVal);
        }
        return String(oldVal || "") !== String(newVal || "");
      };

      const editableFields = [
        "status", "platform", "internalCID", "clientSID", "campaignName",
        "liveDate", "startDate", "endDate", "clientName", "deliveryDay", "pacing",
        "allocation", "accepted", "rejected", "pendingQA", "outstanding", "delivered",
        "priority", "CM", "CSM", "salesManagers", "io_po_number", "so_number",
      ];

      const payload = {};
      editableFields.forEach((field) => {
        if (!isFieldEditable(field)) return; // never send fields this role can't edit
        const newVal = form[field];
        const oldVal = originalCampaign[field];
        if (hasChanged(field, newVal, oldVal)) {
          if (["liveDate", "startDate", "endDate"].includes(field)) {
            payload[field] = toUTCISOString(newVal);
          } else {
            payload[field] = newVal;
          }
        }
      });

      if (Object.keys(payload).length === 0) {
        toast.info("No changes detected");
        setSaving(false);
        return;
      }

      await api.put(`/campaigns/${id}`, payload);
      toast.success("Campaign updated successfully");
      navigate(`/campaign/${id}`);
    } catch (e) {
      console.error("Update error:", e);
      toast.error("Update failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  function toUTCISOString(date) {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return null;
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
  }

  const logout = () => {
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  if (loading) return <div className="p-8 text-center text-xl">Loading campaign…</div>;

  return (
    <div className="mx-auto px-6 py-8">
      <Navbar logout={logout} />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold">Edit Campaign</h1>
        <Button asChild variant="outline">
          <Link to={`/campaign/${id}`}>← Back</Link>
        </Button>
      </div>

{!isAdmin && (
  <p className="text-xs text-muted-foreground mb-4">
    {isCM && !isCSM && "As Campaign Manager, you can only edit Delivered and Accepted here — everything else is read-only."}
    {isCSM && !isCM && "As Client Success Manager, you can edit every field except Delivered and Accepted."}
    {isCM && isCSM && "You hold both Campaign Manager and Client Success Manager roles, so you can edit every field here."}
    {!isCM && !isCSM && "You have view-only access to this campaign."}
  </p>
)}

      <Card className="neon-left neon-card">
        <CardHeader>
          <CardTitle>Edit Campaign Details</CardTitle>
        </CardHeader>

        <CardContent>
          <form
            onSubmit={handleSubmit(onSubmit, () => toast.error("Please fill all required fields"))}
            className="grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            {/* STATUS */}
            <Field label="Status" required>
              <Select
                value={watch("status") || ""}
                disabled={!isFieldEditable("status")}
                onValueChange={(v) => setValue("status", v, { shouldValidate: true })}
              >
                <SelectTrigger><SelectValue placeholder="Select status…" /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <input type="hidden" {...register("status", { required: true })} />
            </Field>

            {/* PLATFORM */}
            <Field label="Platform" required>
              <Select
                value={watch("platform") || ""}
                disabled={!isFieldEditable("platform")}
                onValueChange={(v) => setValue("platform", v, { shouldValidate: true })}
              >
                <SelectTrigger><SelectValue placeholder="Select platform…" /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <input type="hidden" {...register("platform", { required: true })} />
            </Field>

            {/* INTERNAL CID */}
            <Field label="Internal CID">
              <Input
                placeholder="Internal CID"
                disabled={!isFieldEditable("internalCID")}
                className={!isFieldEditable("internalCID") ? disabledCls : ""}
                {...register("internalCID")}
              />
            </Field>

            {/* CLIENT SID */}
            <Field label="Client SID">
              <Input
                placeholder="Client SID"
                disabled={!isFieldEditable("clientSID")}
                className={!isFieldEditable("clientSID") ? disabledCls : ""}
                {...register("clientSID")}
              />
            </Field>

            {/* CAMPAIGN NAME */}
            <Field label="Campaign Name" required>
              <Input
                placeholder="Campaign Name"
                disabled={!isFieldEditable("campaignName")}
                className={!isFieldEditable("campaignName") ? disabledCls : ""}
                {...register("campaignName", { required: true })}
              />
            </Field>

            {/* PRIORITY */}
            <Field label="Priority" required>
              <Select
                value={watch("priority") || ""}
                disabled={!isFieldEditable("priority")}
                onValueChange={(v) => setValue("priority", v, { shouldValidate: true })}
              >
                <SelectTrigger><SelectValue placeholder="Select priority…" /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <input type="hidden" {...register("priority", { required: true })} />
            </Field>

            {/* LIVE DATE */}
            <Field label="Live Date" required>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" disabled={!isFieldEditable("liveDate")} className="justify-start font-normal">
                    {watch("liveDate") ? format(watch("liveDate"), "PPP") : "Pick Live Date…"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <Calendar
                    mode="single"
                    selected={watch("liveDate")}
                    onSelect={(d) => setValue("liveDate", d, { shouldValidate: true })}
                  />
                </PopoverContent>
              </Popover>
              <input type="hidden" {...register("liveDate", { required: true })} />
            </Field>

            {/* START DATE */}
            <Field label="Start Date" required>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" disabled={!isFieldEditable("startDate")} className="justify-start font-normal">
                    {watch("startDate") ? format(watch("startDate"), "PPP") : "Pick Start Date…"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <Calendar
                    mode="single"
                    selected={watch("startDate")}
                    onSelect={(d) => setValue("startDate", d, { shouldValidate: true })}
                  />
                </PopoverContent>
              </Popover>
              <input type="hidden" {...register("startDate", { required: true })} />
            </Field>

            {/* END DATE */}
            <Field label="End Date" required>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" disabled={!isFieldEditable("endDate")} className="justify-start font-normal">
                    {watch("endDate") ? format(watch("endDate"), "PPP") : "Pick End Date…"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <Calendar
                    mode="single"
                    selected={watch("endDate")}
                    onSelect={(d) => setValue("endDate", d, { shouldValidate: true })}
                  />
                </PopoverContent>
              </Popover>
              <input type="hidden" {...register("endDate", { required: true })} />
            </Field>

            {/* CLIENT NAME */}
            <Field label="Client" required>
              <Select
                value={watch("clientName") || ""}
                disabled={!isFieldEditable("clientName")}
                onValueChange={(v) => setValue("clientName", v, { shouldValidate: true })}
              >
                <SelectTrigger><SelectValue placeholder="Select Client…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" {...register("clientName", { required: true })} />
            </Field>

            {/* DELIVERY DAY */}
            <Field label="Delivery Day" required>
              <Select
                value={watch("deliveryDay") || ""}
                disabled={!isFieldEditable("deliveryDay")}
                onValueChange={(v) => setValue("deliveryDay", v, { shouldValidate: true })}
              >
                <SelectTrigger><SelectValue placeholder="Select day…" /></SelectTrigger>
                <SelectContent>
                  {DELIVERY_DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <input type="hidden" {...register("deliveryDay", { required: true })} />
            </Field>

            {/* PACING */}
            <Field label="Pacing">
              <Select
                value={watch("pacing") || ""}
                disabled={!isFieldEditable("pacing")}
                onValueChange={(v) => setValue("pacing", v)}
              >
                <SelectTrigger><SelectValue placeholder="Select pacing…" /></SelectTrigger>
                <SelectContent>
                  {PACING_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>

            {/* ALLOCATION */}
            <Field label="Allocation" required>
              <Input
                type="number"
                min={0}
                onKeyDown={blockNegativeKey}
                onWheel={blockWheelChange}
                placeholder="Allocation"
                disabled={!isFieldEditable("allocation")}
                className={!isFieldEditable("allocation") ? disabledCls : ""}
                {...register("allocation", { required: true, min: 0 })}
              />
            </Field>

            {/* ACCEPTED — Campaign Manager only */}
            <Field label="Accepted" required>
              <Input
                type="number"
                min={0}
                onKeyDown={blockNegativeKey}
                onWheel={blockWheelChange}
                placeholder="Accepted"
                disabled={!isFieldEditable("accepted")}
                className={!isFieldEditable("accepted") ? disabledCls : ""}
                {...register("accepted", { required: true, min: 0 })}
              />
            </Field>

            {/* DELIVERED — Campaign Manager only */}
            <Field label="Delivered">
              <Input
                type="number"
                min={0}
                onKeyDown={blockNegativeKey}
                onWheel={blockWheelChange}
                placeholder="Delivered"
                disabled={!isFieldEditable("delivered")}
                className={!isFieldEditable("delivered") ? disabledCls : ""}
                {...register("delivered", { min: 0 })}
              />
            </Field>

            {/* REJECTED */}
            <Field label="Rejected" required>
              <Input
                type="number"
                min={0}
                onKeyDown={blockNegativeKey}
                onWheel={blockWheelChange}
                placeholder="Rejected"
                disabled={!isFieldEditable("rejected")}
                className={!isFieldEditable("rejected") ? disabledCls : ""}
                {...register("rejected", { required: true, min: 0 })}
              />
            </Field>

            {/* PENDING QA */}
            <Field label="Pending QA" required>
              <Input
                placeholder="Pending QA"
                disabled={!isFieldEditable("pendingQA")}
                className={!isFieldEditable("pendingQA") ? disabledCls : ""}
                {...register("pendingQA", { required: true, min: 0 })}
              />
            </Field>

            {/* OUTSTANDING (read-only, auto-computed — always locked, regardless of role) */}
            <Field label="Outstanding (auto-calculated)">
              <Input
                type="number"
                readOnly
                className="bg-muted/50 cursor-not-allowed"
                value={watch("outstanding") ?? ""}
                {...register("outstanding")}
              />
            </Field>

            {/* IO/PO NUMBER */}
            <Field label="IO / PO Number">
              <Input
                placeholder="IO / PO Number"
                disabled={!isFieldEditable("io_po_number")}
                className={!isFieldEditable("io_po_number") ? disabledCls : ""}
                {...register("io_po_number")}
              />
            </Field>

            {/* SO NUMBER */}
            <Field label="SO Number">
              <Input
                placeholder="SO Number"
                disabled={!isFieldEditable("so_number")}
                className={!isFieldEditable("so_number") ? disabledCls : ""}
                {...register("so_number")}
              />
            </Field>

            {/* CM */}
            <Field label="Campaign Manager (CM)" required>
              <Select
                value={watch("CM") || ""}
                disabled={!isFieldEditable("CM")}
                onValueChange={(v) => setValue("CM", v, { shouldValidate: true })}
              >
                <SelectTrigger><SelectValue placeholder="Select CM…" /></SelectTrigger>
                <SelectContent>
                  {CMs.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <input type="hidden" {...register("CM", { required: true })} />
            </Field>

            {/* CSM */}
            <Field label="Customer Success Manager (CSM)" required>
              <Select
                value={watch("CSM") || ""}
                disabled={!isFieldEditable("CSM")}
                onValueChange={(v) => setValue("CSM", v, { shouldValidate: true })}
              >
                <SelectTrigger><SelectValue placeholder="Select CSM…" /></SelectTrigger>
                <SelectContent>
                  {csmList.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <input type="hidden" {...register("CSM", { required: true })} />
            </Field>

            {/* SALES MANAGERS — multi-select */}
            <Field label="Sales Manager(s)" required>
              <Select
                value=""
                disabled={!isFieldEditable("salesManagers")}
                onValueChange={(v) => {
                  const prev = watch("salesManagers") || [];
                  if (!prev.includes(v)) {
                    setValue("salesManagers", [...prev, v], { shouldValidate: true });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue>
                    {watch("salesManagers")?.length
                      ? `${watch("salesManagers").length} manager(s) selected`
                      : "Select Sales Manager(s)…"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {managers.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <input
                type="hidden"
                {...register("salesManagers", {
                  validate: (v) => v?.length > 0 || "At least one Sales Manager is required",
                })}
              />
            </Field>

            {/* Selected managers chips */}
            <div className="col-span-1 sm:col-span-2">
              {Array.isArray(watch("salesManagers")) && watch("salesManagers").length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {watch("salesManagers").map((name) => (
                    <span
                      key={name}
                      className="flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 text-foreground px-3 py-1 text-sm"
                    >
                      {name}
                      {isFieldEditable("salesManagers") && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = watch("salesManagers").filter((m) => m !== name);
                            setValue("salesManagers", updated, { shouldValidate: true });
                          }}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No managers selected yet</p>
              )}
            </div>

            {/* ACTIONS */}
            <div className="col-span-1 sm:col-span-2 flex justify-end gap-3 mt-4 pt-4 border-t border-border">
              <Button type="button" variant="outline" onClick={() => navigate(`/campaign/${id}`)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <Footer />
    </div>
  );
} 
